import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { loadTodayRank, loadWeeklyRank, loadMonthlyRank } from '../../../lib/api/runningtrackMonitor';
import {
  Card, CardHead, CardTitle, CardBody, Tools, Hint, TableWrap, Tbl, Empty, SegButtons, SmallButton, RangeText, Who, Rank,
} from './ZetaUi';
import ZetaMemberModal from './ZetaMemberModal';
import { fromUnix, addDays, md, km, dur, mapLimit, getMemberInfo, dayStart } from './zetaUtil';

// 랭킹 — 러닝앱 랭킹 화면과 같은 API·같은 칸(오늘/주간/월간, 1~3위 단상, 순위 · 이름 · 시간 · 거리).
//   등수는 서버 값 그대로. 닉네임은 응답의 nickName, 없는 서버에서는 memberinfo 로 채운다(최대 50명).

const Podium = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  align-items: end;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
`;
const PCard = styled.button`
  border: 1px solid ${(p) => (p.$first ? '#f5c542' : '#e0e4ea')};
  border-radius: 12px;
  padding: ${(p) => (p.$first ? '30px 10px 16px' : '16px 10px')};
  background: #f8f9fb;
  display: grid;
  justify-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: inherit;
  color: #2c3e50;
  min-width: 0;
  box-shadow: ${(p) => (p.$first ? '0 2px 8px rgba(0,0,0,0.06)' : 'none')};
  &:hover { border-color: #1A73E9; }
  .nm { font-weight: 700; overflow-wrap: anywhere; }
  .sub { font-size: 11.5px; color: #868e96; }
  .dist { font-size: ${(p) => (p.$first ? '26px' : '22px')}; font-weight: 700; color: #1A73E9; }
  .tm { font-size: 12.5px; color: #868e96; }
`;

const PERIODS = [['today', '오늘'], ['week', '주간'], ['month', '월간']];
const LOADERS = { today: loadTodayRank, week: loadWeeklyRank, month: loadMonthlyRank };

const ZetaRanking = ({ clubID, socketKey }) => {
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  const [nick, setNick] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [openID, setOpenID] = useState(null);

  useEffect(() => {
    if (!clubID) return;
    let alive = true;
    setData(null); setShowAll(false);
    LOADERS[period](clubID)
      .then((res) => {
        const d = res.data || {};
        const body = period === 'today' ? d : d.distance || {};
        const infos = Array.isArray(body.infos) ? body.infos : [];
        let label;
        if (period === 'today') label = md(d.date ? fromUnix(d.date) : dayStart(new Date()));
        else label = `${md(fromUnix(d.startTime))} ~ ${md(addDays(fromUnix(d.endTime), -1))}`;
        if (!alive) return;
        setData({ ok: d.result === 1, infos, total: body.total || 0, label });
        // 랭킹 응답에 nickName 이 오면(서버가 member.nickName 을 싣는 버전, TV 앱과 같음) 그대로 쓴다.
        //   안 오는 서버에서만 회원마다 memberinfo 로 채운다.
        if (infos.length && infos[0].nickName !== undefined) return;
        mapLimit(infos, 6, async (x) => {
          const info = await getMemberInfo(x.memberID, clubID);
          if (alive && info) setNick((prev) => ({ ...prev, [x.memberID]: info.nickName || '' }));
        });
      })
      .catch(() => alive && setData({ ok: false, infos: [], total: 0, label: '' }));
    return () => { alive = false; };
  }, [clubID, period]);

  const infos = data ? data.infos : [];
  const podium = infos.slice(0, 3);
  const rest = infos.slice(3);
  const shown = showAll ? rest : rest.slice(0, 12);
  const nickOf = (x) => (x.nickName !== undefined ? x.nickName : nick[x.memberID]);
  const card = (x, first) => x ? (
    <PCard type="button" $first={!!first} onClick={() => setOpenID(x.memberID)} title="회원 상세 보기">
      <Rank n={x.rank}>{x.rank}</Rank>
      <span className="nm">{nickOf(x) || x.name}</span>
      <span className="sub">{x.name} · 회원번호 {x.memberID}</span>
      <span className="dist">{km(x.distance)}</span>
      <span className="tm">{dur(x.time)}</span>
    </PCard>
  ) : <div />;

  return (
    <Card>
      <CardHead>
        <CardTitle><span role="img" aria-label="랭킹">🏆</span> 랭킹 · 우리 헬스장</CardTitle>
        <Tools>
          <SegButtons options={PERIODS} value={period} onChange={setPeriod} />
          {data ? <RangeText>{data.label} · 참여 {data.total}명</RangeText> : null}
        </Tools>
      </CardHead>
      <CardBody>
        <Hint>러닝앱 랭킹과 같은 기준입니다(거리순, 거리가 같으면 시간이 짧은 순). 지금 기간만 볼 수 있고 상위 50명까지 표시합니다. 회원을 누르면 상세가 열립니다.</Hint>
        {!data ? <Empty>불러오는 중…</Empty> : !data.ok ? <Empty>랭킹을 불러오지 못했습니다</Empty> : !infos.length ? <Empty>아직 기록이 없습니다</Empty> : (
          <>
            <Podium>{card(podium[1])}{card(podium[0], true)}{card(podium[2])}</Podium>
            {rest.length ? (
              <TableWrap>
                <Tbl>
                  <thead><tr><th>순위</th><th>닉네임 / 이름 · 회원번호</th><th className="r">시간</th><th className="r">거리</th></tr></thead>
                  <tbody>
                    {shown.map((x) => (
                      <tr key={x.memberID} className="click" onClick={() => setOpenID(x.memberID)}>
                        <td><Rank n={0}>{x.rank}</Rank></td>
                        <td><Who name={x.name} main={nickOf(x) || x.name} sub={`${x.name} · 회원번호 ${x.memberID}`} /></td>
                        <td className="r">{dur(x.time)}</td>
                        <td className="r strong">{km(x.distance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tbl>
              </TableWrap>
            ) : null}
            {rest.length > 12 ? (
              <div style={{ textAlign: 'center' }}>
                <SmallButton type="button" onClick={() => setShowAll(!showAll)}>{showAll ? '15위까지만 보기' : `${infos.length}위까지 보기`}</SmallButton>
              </div>
            ) : null}
            {data.total > infos.length ? <Hint style={{ textAlign: 'center' }}>상위 {infos.length}명까지만 표시합니다 (전체 {data.total}명)</Hint> : null}
          </>
        )}
      </CardBody>
      <ZetaMemberModal open={!!openID} memberID={openID} clubID={clubID} socketKey={socketKey} onClose={() => setOpenID(null)} />
    </Card>
  );
};

export default ZetaRanking;
